import { getCartSuccessLabel } from '../shared/cart-feedback-label';
import {
  QUANTITY_STEPPER_CHANGE_EVENT,
  mountQuantityStepper,
  type QuantityStepperInstance
} from '../shared/quantity-stepper';
import { scrollToConfigurator } from '../shared/scroll-to-configurator';
import { initToggletipGroup } from '../shared/toggletip';

const ADD_TO_CART_TIMEOUT_MS = 15000;
const SUCCESS_RESET_MS = 1600;

interface EligibilityRow {
  ProjectWiseQty: number;
  ConnectLicensesQty: number;
}

interface AddToCartResult {
  requestId: string;
  success: boolean;
  error?: string;
}

interface HiddenVariant {
  sku: string;
  bundleUid: string;
}

function parseEligibility(json: string | null | undefined): EligibilityRow[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// Components with no UI row (ShowVariant=false) but still owed in the cart payload.
function parseHiddenVariants(json: string | null | undefined): HiddenVariant[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// Matrix decreases as PW qty rises (real payload, BSBOPHA2-1691) — take the
// highest applicable row, not a direct index lookup.
export function requiredConnectQty(eligibility: EligibilityRow[], projectWiseQty: number): number {
  const applicable = eligibility
    .filter(row => row.ProjectWiseQty <= projectWiseQty)
    .sort((a, b) => b.ProjectWiseQty - a.ProjectWiseQty);
  return applicable[0]?.ConnectLicensesQty ?? 0;
}

export function meetsRequiredConnectQuantity(
  requiredQuantity: number,
  connectSelected: boolean,
  connectQuantity: number
): boolean {
  return requiredQuantity <= 0 || (connectSelected && connectQuantity >= requiredQuantity);
}

const BUNDLE_SELECTION_UID = /^(bundle\/\d+\/\d+)\/\d+$/;

// Magento bundle selection UIDs are base64("bundle/<optionId>/<selectionId>/<qty>"),
// so the stepper quantity has to be baked into the UID itself. Any UID that
// doesn't decode to that shape is sent untouched.
export function withBundleQuantity(uid: string, quantity: number): string {
  try {
    const match = window.atob(uid).match(BUNDLE_SELECTION_UID);
    return match ? window.btoa(`${match[1]}/${quantity}`) : uid;
  } catch {
    return uid;
  }
}

function parsePrice(value: string | undefined): number {
  const parsed = Number.parseFloat((value ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

// Mirrors ProductPriceService.FormatCurrency: page locale for grouping/decimals,
// ISO currency code for the symbol and its position.
function formatCurrency(amount: number, currency: string | undefined): string {
  const locale = document.documentElement.lang || 'en';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'USD',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch {
    return amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}

export function closeContainingDialog(panel: HTMLElement): void {
  const dialog = panel.closest<HTMLDialogElement>('dialog');
  if (dialog?.open) dialog.close();
}

function mountConfiguratorPanel(panel: HTMLElement): void {
  if (panel.dataset.cartMounted === 'true') return;
  panel.dataset.cartMounted = 'true';

  const eligibility = parseEligibility(
    panel.querySelector<HTMLScriptElement>('[data-eligibility-config]')?.textContent
  );
  const hiddenVariants = parseHiddenVariants(
    panel.querySelector<HTMLScriptElement>('[data-hidden-variants-config]')?.textContent
  );
  const minimumSpendAmount = Number.parseFloat(panel.dataset.minimumSpendAmount ?? '0') || 0;

  const pwRow = Array.from(
    panel.querySelectorAll<HTMLElement>('[data-option-row][data-child-sku]')
  ).find(row => !row.closest('[data-connect-row]'));
  // Scoped to this panel, not a hardcoded id — Razor gives each instance its own id.
  const connectToggle = panel.querySelector<HTMLInputElement>('input[type="checkbox"]');
  const connectRow = panel.querySelector<HTMLElement>('[data-connect-row]');
  const connectOptionRow = connectRow?.querySelector<HTMLElement>('[data-option-row]');
  const connectStepperRoot = connectRow?.querySelector<HTMLElement>('[data-quantity-stepper]');
  const regionSelect = panel.querySelector<HTMLSelectElement>(
    'select[name="data-storage-location"]'
  );
  const addButton = panel.querySelector<HTMLButtonElement>('[data-add-to-cart]');
  const buttonLabel = addButton?.querySelector<HTMLElement>('[data-button-label]');
  const feedback = panel.querySelector<HTMLElement>('[data-product-options-feedback]');
  const status = panel.querySelector<HTMLElement>('[data-product-options-status]');
  const minSpendFeedback = panel.querySelector<HTMLElement>('[data-minimum-spend-feedback]');
  const priceOutput = panel.querySelector<HTMLElement>('[data-configurator-price]');

  const pwStepperRoot = pwRow?.querySelector<HTMLElement>('[data-quantity-stepper]');
  if (!pwRow || !pwStepperRoot || !addButton || !buttonLabel) return;

  const pwStepper = mountQuantityStepper(pwStepperRoot);
  const pwPrice = parsePrice(pwRow.dataset.childPrice);
  const connectPrice = connectOptionRow ? parsePrice(connectOptionRow.dataset.childPrice) : 0;

  let connectStepper: QuantityStepperInstance | null = null;
  if (connectStepperRoot) {
    connectStepper = mountQuantityStepper(connectStepperRoot);
  }

  const defaultLabel = buttonLabel.textContent?.trim() || '';
  let pendingRequestId: string | null = null;
  let loadTimeout: number | null = null;
  let successTimeout: number | null = null;

  const clearFeedback = (): void => {
    if (feedback) {
      feedback.hidden = true;
      feedback.textContent = '';
      feedback.removeAttribute('data-feedback-state');
    }
    buttonLabel.textContent = defaultLabel;
    addButton.removeAttribute('data-feedback-state');
  };

  const syncState = (): void => {
    const pwQty = pwStepper?.getQuantity() ?? 0;
    const required = requiredConnectQty(eligibility, pwQty);
    const connectSelected = Boolean(connectToggle?.checked);
    if (connectRow) connectRow.hidden = !connectSelected;

    const connectQty = connectSelected ? (connectStepper?.getQuantity() ?? 0) : 0;
    const total = pwQty * pwPrice + connectQty * connectPrice;

    if (priceOutput)
      priceOutput.textContent = formatCurrency(total, panel.dataset.currency?.trim());

    const meetsMinimumSpend =
      meetsRequiredConnectQuantity(required, connectSelected, connectQty) &&
      (minimumSpendAmount <= 0 || total >= minimumSpendAmount);
    if (minSpendFeedback) minSpendFeedback.hidden = meetsMinimumSpend;

    const regionUid = regionSelect?.selectedOptions[0]?.dataset.optionUid?.trim();
    regionSelect?.setAttribute('aria-invalid', regionUid ? 'false' : 'true');

    const hasValidSelection =
      pwQty > 0 &&
      Boolean(panel.dataset.sku?.trim()) &&
      Boolean(pwRow.dataset.optionUid?.trim()) &&
      Boolean(regionUid) &&
      meetsMinimumSpend;
    if (!pendingRequestId) addButton.disabled = !hasValidSelection;
  };

  const handleChange = (): void => {
    if (successTimeout !== null) {
      window.clearTimeout(successTimeout);
      successTimeout = null;
    }
    if (status) status.textContent = '';
    clearFeedback();
    syncState();
  };

  panel.addEventListener(QUANTITY_STEPPER_CHANGE_EVENT, handleChange);
  connectToggle?.addEventListener('change', handleChange);
  regionSelect?.addEventListener('change', handleChange);

  addButton.addEventListener('click', () => {
    if (addButton.disabled || pendingRequestId) return;

    const pwQty = pwStepper?.getQuantity() ?? 0;
    const connectSelected = Boolean(connectToggle?.checked);
    const connectQty = connectSelected ? (connectStepper?.getQuantity() ?? 0) : 0;
    const regionUid = regionSelect?.selectedOptions[0]?.dataset.optionUid?.trim();
    const parentSku = panel.dataset.sku?.trim();
    const pwBundleUid = pwRow.dataset.optionUid?.trim();
    const connectBundleUid = connectOptionRow?.dataset.optionUid?.trim();

    if (!parentSku || !pwBundleUid || !regionUid || (connectSelected && !connectBundleUid)) {
      syncState();
      return;
    }

    clearFeedback();
    addButton.disabled = true;
    addButton.dataset.feedbackState = 'loading';
    buttonLabel.textContent = defaultLabel;

    const requestId = `projectwise-flex-bundle-${Date.now()}`;
    pendingRequestId = requestId;

    // Same contract as the regular ProjectWise bundle: one parent line, every
    // component + region packed into selected_options.
    const items = [
      {
        sku: parentSku,
        quantity: 1,
        selected_options: [
          withBundleQuantity(pwBundleUid, pwQty),
          ...(connectSelected && connectBundleUid
            ? [withBundleQuantity(connectBundleUid, connectQty)]
            : []),
          ...hiddenVariants.map(v => v.bundleUid).filter(Boolean),
          regionUid
        ]
      }
    ];

    const onResult = (event: Event): void => {
      const result = (event as CustomEvent<AddToCartResult>).detail;
      if (result.requestId !== requestId) return;

      if (loadTimeout !== null) {
        window.clearTimeout(loadTimeout);
        loadTimeout = null;
      }
      document.removeEventListener('bentley:add-to-cart-result', onResult);
      pendingRequestId = null;

      if (result.success) {
        addButton.dataset.feedbackState = 'success';
        const successLabel = getCartSuccessLabel(panel, defaultLabel);
        buttonLabel.textContent = successLabel;
        if (status) status.textContent = successLabel;

        closeContainingDialog(panel);

        successTimeout = window.setTimeout(() => {
          successTimeout = null;
          clearFeedback();
          if (status) status.textContent = '';
          syncState();
        }, SUCCESS_RESET_MS);
        return;
      }

      clearFeedback();
      if (feedback) {
        feedback.hidden = false;
        feedback.dataset.feedbackState = 'error';
        feedback.textContent = result.error || 'Unable to add this bundle right now.';
      }
      if (status)
        status.textContent = `Unable to add ${panel.dataset.planName || 'bundle'} to cart.`;
      syncState();
    };

    document.addEventListener('bentley:add-to-cart-result', onResult);
    loadTimeout = window.setTimeout(() => {
      document.removeEventListener('bentley:add-to-cart-result', onResult);
      pendingRequestId = null;
      handleChange();
      if (feedback) {
        feedback.hidden = false;
        feedback.dataset.feedbackState = 'error';
        feedback.textContent = 'Unable to add this bundle right now.';
      }
    }, ADD_TO_CART_TIMEOUT_MS);

    document.dispatchEvent(
      new CustomEvent('bentley:add-to-cart-request', {
        detail: { requestId, planName: panel.dataset.planName?.trim() || 'bundle', items }
      })
    );
  });

  syncState();
}

export default function projectwiseBundleFlexPricingOptionsBlock(root: HTMLElement): void {
  initToggletipGroup(root, { idPrefix: 'projectwise-flex-note', describedBy: true });

  const panels = Array.from(root.querySelectorAll<HTMLElement>('[data-product-options-panel]'));
  panels.forEach(mountConfiguratorPanel);

  const configurator = root.querySelector<HTMLElement>('[data-pricing-configurator]');
  const previousButton = configurator?.querySelector<HTMLButtonElement>('[data-options-previous]');
  const inlineConfigureButton = root.querySelector<HTMLButtonElement>('[data-pricing-configure]');
  const summary = root.querySelector<HTMLElement>('[data-pricing-summary]');

  if (!configurator) return;

  previousButton?.addEventListener('click', () => {
    const dialog = configurator.closest<HTMLDialogElement>('dialog');
    if (dialog) {
      dialog.close();
      return;
    }

    configurator.hidden = true;
    if (summary) summary.hidden = false;
    inlineConfigureButton?.focus();
  });

  inlineConfigureButton?.addEventListener('click', () => {
    if (summary) summary.hidden = true;
    configurator.hidden = false;
    configurator.focus({ preventScroll: true });
    window.requestAnimationFrame(() => scrollToConfigurator(configurator));
  });
}
